/**
 * Practical email validation for client-entered addresses.
 *
 * This matches Zod's default `z.email()` check ("Practical email validation"),
 * which covers the addresses virtually everyone uses (plus-tags, dots in the
 * local part, multi-label domains) without attempting full RFC 5322 / IDN
 * compliance. Rejects common junk like missing TLDs (`a@b`), consecutive dots,
 * leading/trailing dots in the local part, and bare hostnames (`user@localhost`).
 *
 * Derived from Zod v4.4.3:
 * https://github.com/colinhacks/zod/blob/v4.4.3/packages/zod/src/v4/core/regexes.ts#L51-L53
 *
 * Zod also ships stricter/looser alternatives (`html5Email`, `rfc5322Email`,
 * `unicodeEmail`) in that same file if requirements change later.
 */
const PRACTICAL_EMAIL_PATTERN =
    /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/;

/**
 * Returns whether `email` looks like a normal, deliverable-shaped address.
 * Empty strings are invalid — callers that treat blank as optional should
 * check for emptiness before calling this.
 */
export function isValidEmail(email: string): boolean {
    return PRACTICAL_EMAIL_PATTERN.test(email);
}
